import { MutableRefObject, useEffect, useMemo, useRef, useState } from "react";
import { CodeSQLEditor, SQLEditorRef } from "../../../components/codeEditor";
import styles from "../styles.module.scss";
import classNames from "classnames";
import { Tab } from "../../../components/tab";
import { InsightSection } from "@/modules/insights";
import { SimpleInput } from "../../../components/form";
import {
  Card,
  CardContent,
  ScrollArea,
  Stack,
  Tooltip,
  Typography,
  Markdown,
} from "@altimateai/lego";
import { CloseIcon, WarningIcon } from "@assets/icons";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import SingleLineEllipses from "@/uiCore/components/singleLineEllipses/SingleLineEllipses";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { GetOpportunitiesDataResponse, GetQuerySQLLineageResponse } from "@/pages/explorer/types";
import { SqlLineageNodeDetail } from "@/pages/query/components/QuerySqlLineage";

export interface ColumnItem {
  name: string;
  expression?: string;
  description?: string;
}

type OpportunityWithLegacyFields = GetOpportunitiesDataResponse & {
  parent_indices?: Parameters<SQLEditorRef["setHighlight"]>[0];
  markdown_description?: string;
  reason?: string;
  id?: string;
};

interface SideContentProps {
  selectedNode: string;
  query: string;
  sqlLineage?: GetQuerySQLLineageResponse;
  parentEditorRef: MutableRefObject<SQLEditorRef | null>;
  selectedTab?: string;
  closeSideContent?: () => void;
  opportunities?: OpportunityWithLegacyFields[];
  startDate: Date;
  endDate: Date;
  hideCloseButton?: boolean;
  maxHeight?: string;
}

function cleanNodeName(str: string) {
  return str.replace(/_(\d{10,})$/g, "");
}

export const ColumnSection = ({ columns }: { columns: ColumnItem[] }) => {
  const [filteredColumn, setFilteredColumn] = useState([...columns]);

  if (columns.length === 0) {
    return (
      <Stack direction="column">
        <Typography variant="body" className="al-text-muted-foreground">
          No columns
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack direction="column">
      <div className={classNames(styles.card, "mb-0 flex-grow column-section")}>
        <Stack direction="column" gap="sm" className="al-h-full">
          <Stack justify="between" align="center" gap="xs">
            <Typography variant="section-heading">Column</Typography>
          </Stack>
          <SimpleInput
            size="sm"
            placeholder="Search by column name"
            onChange={(v: string) => {
              const _search = v.toLowerCase();
              setFilteredColumn(
                columns.filter((c: ColumnItem) => c.name.toLowerCase().includes(_search))
              );
            }}
          />
          <Stack align="center" gap="xs">
            <Typography variant="body">{filteredColumn?.length} columns</Typography>
          </Stack>
          <Stack direction="column" gap="sm" className="overflow-y">
            {filteredColumn?.map(_column => (
              <div key={_column.name} className={classNames(styles.column_card, "al-min-w-0")}>
                <Stack justify="between" align="center" gap="xs">
                  <div className="al-truncate">{_column.name}</div>
                </Stack>
                {_column.expression && (
                  <>
                    <div className="divider" />
                    <div className="al-min-w-0 al-overflow-hidden">
                      <SyntaxHighlighter
                        style={vscDarkPlus}
                        language="sql"
                        wrapLines={true}
                        wrapLongLines={true}
                        PreTag="div"
                        customStyle={{
                          margin: 0,
                          maxWidth: "100%",
                          maxHeight: "150px",
                          overflowY: "auto",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                        }}
                      >
                        {_column.expression}
                      </SyntaxHighlighter>
                    </div>
                  </>
                )}
              </div>
            ))}
          </Stack>
        </Stack>
      </div>
    </Stack>
  );
};

export function SideContent({
  selectedNode,
  query,
  sqlLineage,
  parentEditorRef,
  selectedTab = "",
  closeSideContent,
  opportunities,
  startDate,
  endDate,
  hideCloseButton = false,
  maxHeight,
}: SideContentProps) {
  const editorRef = useRef<SQLEditorRef>(null);
  const { searchParams, updateSearchParamsByKey } = useFiltersBySearchParams();
  const setSelectedInsightId = (v: string) => updateSearchParamsByKey({ opportunityId: v });
  const opportunityId = searchParams.get("opportunityId");
  const selectedOpportunity = opportunities?.find(
    (o: OpportunityWithLegacyFields) => o.opportunity_id === opportunityId
  );
  const _columns = useMemo(() => {
    const nodeDetail = sqlLineage?.details?.[selectedNode] as SqlLineageNodeDetail | undefined;
    return (
      nodeDetail?.columns?.map(item => ({
        ...item,
        description: item.expression,
      })) ?? []
    );
  }, [selectedNode, sqlLineage?.details]);

  const tabs = [
    {
      label: "Insights",
      component: (
        <InsightSection
          sideContentMode={true}
          opportunities={opportunities ?? []}
          selectedOpportunityId={opportunityId}
          setOpportunityId={(item: string) => {
            const opportunity = opportunities?.find(
              (o: OpportunityWithLegacyFields) => o.opportunity_id === item
            );
            if (opportunity?.id) setSelectedInsightId(opportunity.id);
          }}
          startDate={startDate}
          endDate={endDate}
          disableNewUI
        />
      ),
    },
    {
      label: `Columns (${_columns.length})`,
      component: <ColumnSection columns={_columns} />,
    },
  ];

  useEffect(() => {
    if (selectedTab !== "Code") return;
    parentEditorRef.current?.removeHighlight();
    const indices = selectedOpportunity?.parent_indices;
    if (!indices) return;
    // @ts-expect-error keeping the legacy code
    parentEditorRef.current?.setHighlight(indices.map(index => ({ index })));
    // @ts-expect-error keeping the legacy code
    parentEditorRef.current?.highlightSpan({ index: indices });
  }, [parentEditorRef, selectedOpportunity?.parent_indices, selectedTab]);

  useEffect(() => {
    const highlightCode = (indices: Parameters<SQLEditorRef["setHighlight"]>[0]) => {
      editorRef.current?.removeHighlight();
      // @ts-expect-error keeping the legacy code
      editorRef.current?.setHighlight(indices.map(index => ({ index })));
      // @ts-expect-error keeping the legacy code
      editorRef.current?.highlightSpan({ index: indices[0] });
    };

    const nodeDetail = selectedNode
      ? (sqlLineage?.details?.[selectedNode] as SqlLineageNodeDetail | undefined)
      : undefined;
    const rawIndices = selectedNode ? nodeDetail?.indices : selectedOpportunity?.parent_indices;
    editorRef.current?.removeHighlight();
    if (rawIndices) {
      setTimeout(
        () => highlightCode(rawIndices as Parameters<SQLEditorRef["setHighlight"]>[0]),
        100
      );
    }
  }, [opportunities, opportunityId, selectedNode, selectedOpportunity?.parent_indices, sqlLineage]);

  const headerTitle =
    selectedOpportunity?.opportunity_name || (selectedNode ? cleanNodeName(selectedNode) : "");
  const header = (
    <Stack direction="row" className="al-justify-between al-items-center al-p-4 al-border-b">
      {headerTitle ? <SingleLineEllipses content={headerTitle} /> : null}
      {!hideCloseButton && closeSideContent && (
        <button
          onClick={closeSideContent}
          className="al-p-2 al-rounded-md hover:al-bg-gray-100 al-transition-colors"
          aria-label="Close"
        >
          <CloseIcon className="al-h-4 al-w-4" />
        </button>
      )}
    </Stack>
  );

  const scrollableContent = (
    <Stack
      direction="column"
      className="al-p-4 al-min-w-0 al-overflow-hidden"
      style={{ maxWidth: "100%" }}
    >
      {query && sqlLineage?.is_lineage && (
        <div className="al-min-w-0 al-overflow-hidden" style={{ maxWidth: "100%" }}>
          <div style={{ maxWidth: "100%", overflow: "hidden" }}>
            <CodeSQLEditor value={query} height="28vh" ref={editorRef} showCopy lineNumbers />
          </div>
        </div>
      )}
      {selectedOpportunity?.opportunity_name && (
        <div className="al-rounded-md al-bg-gray-10 al-min-w-0" style={{ maxWidth: "100%" }}>
          <Stack justify="between" align="center" className="p-2">
            <Typography variant="section-heading" className="al-truncate">
              Details: &quot;{selectedOpportunity?.opportunity_name}&quot;
            </Typography>
          </Stack>
          <div className="divider" />
          <div
            className="al-bg-white al-p-3 al-rounded-md al-m-2 al-min-w-0"
            style={{ maxWidth: "calc(100% - 16px)" }}
          >
            <Stack justify="between" align="center">
              <Typography variant="h4" weight="medium">
                {selectedOpportunity?.opportunity_name === "potential_query_improvements"
                  ? "Optimized Query"
                  : "Problem"}
              </Typography>
              {selectedOpportunity?.opportunity_name !== "potential_query_improvements" &&
                !selectedOpportunity?.parent_indices && (
                  <Tooltip
                    title={
                      "Query mapping incomplete: The opportunity details may reference expanded " +
                      "views or temporary objects not present in the original query. Please review " +
                      "the full query execution plan for more context."
                    }
                  >
                    <Stack align="center" className="icon-xs">
                      <WarningIcon className="al-h-4 al-w-4" />
                    </Stack>
                  </Tooltip>
                )}
            </Stack>
            <div className={`${styles.markdown_description} al-min-w-0`}>
              <Markdown
                content={
                  selectedOpportunity?.markdown_description ||
                  selectedOpportunity?.description ||
                  selectedOpportunity?.reason ||
                  ""
                }
              />
            </div>
          </div>
        </div>
      )}
      <div className="al-min-w-0 al-overflow-hidden" style={{ maxWidth: "100%" }}>
        <Tab tabs={tabs} pagedTab={false} onSelectedTabChange={() => {}} />
      </div>
    </Stack>
  );

  // When hideCloseButton is true (drawer mode), don't wrap in Card
  if (hideCloseButton) {
    return (
      <Stack direction="column" className="al-h-[90dvh] al-min-w-0">
        <div className="al-overflow-auto al-flex-1 al-min-w-0">{scrollableContent}</div>
      </Stack>
    );
  }

  // Normal desktop mode with Card wrapper
  // if maxHeight is provided, use it with ScrollArea
  if (maxHeight) {
    return (
      <Card className="mb-0 al-min-w-[0]">
        <Stack direction="column" className="al-min-w-0" style={{ height: `${maxHeight}` }}>
          {header}
          <ScrollArea className="al-flex-1 al-min-w-0">
            <div className="al-p-4 al-min-w-0">{scrollableContent}</div>
          </ScrollArea>
        </Stack>
      </Card>
    );
  }

  return (
    <Card className="mb-0 al-min-w-[0]" aria-label="query-details-side-content">
      <CardContent className="al-min-w-0 al-overflow-hidden">
        {header}
        {scrollableContent}
      </CardContent>
    </Card>
  );
}
